/**
 * Wire-mapping for the access-discovery inventory (OIDCPAM Gate-6 Phase 6b —
 * GovernanceDiscovery.tsx).
 *
 * `AdminListDiscoveryResp.items` is typed `Array<Record<string, unknown>>` in
 * the generated client (gen/client/react/client.ts) — the Go handler
 * (AdminGovernanceListDiscovery in admin_governance_server.go) marshals
 * `[]*DiscoveredAccess` directly, bypassing the genapi response-schema
 * encoder, so the generator could not infer a concrete response type. The
 * wire JSON uses snake_case json tags (see pam_discovery.go's
 * DiscoveredAccess struct: id, tenant_id, subject_id, resource_id,
 * grant_kind, lifecycle_state, flags, discovered_at, ...), and the generated
 * client's fromWire() converts EVERY response key snake_case->camelCase
 * before resolving — including nested Record<string, unknown> array items,
 * since the conversion is a blind recursive key-walk, not schema-aware. So
 * the REAL runtime shape at this boundary is WireDiscoveredAccess below
 * (camelCase), NOT the hand-written snake_case DiscoveredAccess type in
 * ../api/types.ts.
 *
 * LATENT BUG FIXED (Gate-6 shared-doc bug class): the pre-migration page
 * cast `data.items` straight to the snake_case `DiscoveredAccess[]` via
 * `as unknown as` and read r.subject_id/r.resource_id/r.grant_kind/
 * r.discovered_at off it — every one of those keys is `undefined` on the
 * actual camelCase runtime object, so the Subject ID, Resource ID, Grant
 * Kind, and Discovered At columns rendered BLANK in production. This
 * mapping maps camelCase -> camelCase field-for-field onto
 * @devnw/cyber-iam's iam/types#DiscoveredAccessRecord shape, pre-formatting
 * discoveredAt as a display string (the library never parses dates itself).
 */
import type {
  DiscoveredAccessRecord,
  DiscoveryFlag,
} from '@devnw/cyber-iam/iam/types'

/** The real (camelCase) runtime shape of one AdminListDiscoveryResp item. */
export interface WireDiscoveredAccess {
  id: string
  subjectId: string
  resourceId: string
  grantKind?: string
  lifecycleState?: string
  flags?: DiscoveryFlag[]
  discoveredAt: string
}

/** HOST-COMPUTED display formatting — the library never parses dates. */
function fmtTime(iso?: string): string {
  return iso ? new Date(iso).toLocaleString() : '—'
}

export function toDiscoveredAccessRecord(
  w: WireDiscoveredAccess
): DiscoveredAccessRecord {
  return {
    id: w.id,
    subjectId: w.subjectId,
    resourceId: w.resourceId,
    grantKind: w.grantKind ?? '',
    flags: w.flags ?? [],
    lifecycleState: w.lifecycleState || undefined,
    discoveredAt: fmtTime(w.discoveredAt),
  }
}
